import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import {
    Search, Download, UserPlus, Wallet, AlertCircle, ChevronLeft, ChevronRight, ChevronRight as Go,
    Users, Crown, Loader2, X, FileUp, RefreshCw, ArrowDownUp, UserRoundPlus,
} from 'lucide-react';
import styles from './Clients.module.css';
import { useStore } from '../../context/StoreContext';
import { useAuth } from '../../context/AuthContext';
import { SMIIFY_API } from '../../config/api';
import BulkImportClients from './BulkImportClients/BulkImportClients';

// =============================================================================
// Client directory
// The server sends one page at a time (search, filter and sort run on the server),
// so the list opens fast even with thousands of clients. The last page seen is kept
// in sessionStorage and shown instantly while the fresh one loads.
// =============================================================================
const CUSTOMERS_API = `${SMIIFY_API}/customers`;
const PAGE_SIZES = [50, 100, 200];
const FILTERS = [
    { key: 'all', label: 'All clients', stat: 'totalClients' },
    { key: 'due', label: 'Udhaar', stat: 'dueCount' },
    { key: 'wallet', label: 'Wallet balance', stat: 'walletCount' },
    { key: 'vip', label: 'Active plans', stat: 'vipCount' },
    { key: 'new', label: 'New (30 days)', stat: 'newCount' },
];
const SORTS = [
    { key: 'recent', label: 'Last visit' },
    { key: 'newest', label: 'Newest added' },
    { key: 'name', label: 'Name A–Z' },
    { key: 'spend', label: 'Highest spend' },
    { key: 'visits', label: 'Most visits' },
    { key: 'due', label: 'Highest udhaar' },
];

const money = (value) => `₹${Math.round(Number(value) || 0).toLocaleString('en-IN')}`;
const maskPhone = (p) => { const s = String(p || ''); return s.length >= 6 ? `${s.slice(0, 2)}${'•'.repeat(s.length - 4)}${s.slice(-2)}` : '••••'; };
const prettyPhone = (p) => { const s = String(p || ''); return s.length === 10 ? `${s.slice(0, 5)} ${s.slice(5)}` : s; };
const formatDate = (iso) => (iso ? new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(iso)) : '');
const ago = (iso) => {
    if (!iso) return 'No visit yet';
    const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
    if (days <= 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 30) return `${days} days ago`;
    if (days < 365) return `${Math.floor(days / 30)} mo ago`;
    return `${Math.floor(days / 365)} yr ago`;
};
const AVATAR_TONES = ['#673de6', '#009e5b', '#fc5185', '#331c74', '#c46c00', '#7b66ff'];
const toneOf = (name) => AVATAR_TONES[[...String(name || '?')].reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_TONES.length];
const initials = (name) => String(name || '?').replace(/^(mr|ms|mrs|dr)\.?\s+/i, '').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?';

const cacheKey = (storeId, params) => `clients:${storeId}:${params}`;
const readCache = (key) => { try { return JSON.parse(sessionStorage.getItem(key) || 'null'); } catch (e) { return null; } };
const writeCache = (key, value) => { try { sessionStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* storage full: skip */ } };

const Clients = () => {
    const navigate = useNavigate();
    const { activeStore } = useStore();
    const { user, hasPermission, constraint } = useAuth();
    const storeId = activeStore?._id || activeStore?.id;
    const isBranch = Boolean(storeId) && storeId !== 'global_hq';

    // Live access (the server checks the same rules)
    const isOwner = user?.role === 'SUPERADMIN';
    const canCreate = hasPermission('customers', 'create');
    const canImport = isOwner || hasPermission('customers', 'import');
    const canImportUpdate = isOwner || constraint('customers', 'allowImportUpdate', false) === true;
    const canSeePhone = hasPermission('customers', 'view_phone');
    const canSeeSpend = hasPermission('customers', 'view_spend');
    const canExport = isOwner || constraint('customers', 'allowExport', false) === true;

    // list state
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [pages, setPages] = useState(1);
    const [stats, setStats] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(100);
    const [search, setSearch] = useState('');
    const [query, setQuery] = useState('');            // search after typing stops
    const [filter, setFilter] = useState('all');
    const [sort, setSort] = useState('recent');
    const [firstLoad, setFirstLoad] = useState(true);
    const [fetching, setFetching] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [statsKey, setStatsKey] = useState(0);        // bump to refresh the cards
    const [exporting, setExporting] = useState(false);

    // add / import
    const [addOpen, setAddOpen] = useState(false);
    const [importOpen, setImportOpen] = useState(false);
    const [form, setForm] = useState({ name: '', phone: '', email: '', gender: '' });
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState('');

    const abortRef = useRef(null);
    const scrollRef = useRef(null);
    const pageRef = useRef(null);
    const [pageHeight, setPageHeight] = useState(null);

    // The page takes exactly the height left below the top bar, whatever the layout is,
    // so only the list scrolls (no window scroll, no guessed top bar size).
    useLayoutEffect(() => {
        let frame = 0;
        const fit = () => {
            const el = pageRef.current;
            if (!el) return;
            const top = el.getBoundingClientRect().top + (document.scrollingElement?.scrollTop || 0);
            let h = Math.max(420, Math.floor(window.innerHeight - top));
            el.style.height = `${h}px`;
            const extra = (document.scrollingElement?.scrollHeight || 0) - window.innerHeight;   // parent padding below us
            if (extra > 0) h = Math.max(420, h - extra);
            setPageHeight(h);
        };
        const onResize = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(fit); };
        fit();
        window.addEventListener('resize', onResize);
        return () => { cancelAnimationFrame(frame); window.removeEventListener('resize', onResize); };
    }, []);
    const headers = useMemo(() => ({ Authorization: `Bearer ${localStorage.getItem('smiify_token')}`, 'x-store-id': storeId }), [storeId]);

    // wait until typing stops before searching
    useEffect(() => {
        const t = setTimeout(() => { setQuery(search.trim()); setPage(1); }, 300);
        return () => clearTimeout(t);
    }, [search]);

    // new branch: start over
    useEffect(() => { setPage(1); setStats(null); setFirstLoad(true); setStatsKey((k) => k + 1); }, [storeId]);

    const params = useMemo(() => new URLSearchParams({ page: String(page), limit: String(pageSize), q: query, filter, sort }).toString(), [page, pageSize, query, filter, sort]);

    const loadPage = useCallback(async ({ withStats = false } = {}) => {
        if (!isBranch) { setFirstLoad(false); setRows([]); setTotal(0); return; }
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        // show the last copy of this exact page straight away
        const key = cacheKey(storeId, params);
        const cached = readCache(key);
        if (cached) {
            setRows(cached.data); setTotal(cached.total); setPages(cached.pages);
            if (cached.stats && !stats) setStats(cached.stats);
            setFirstLoad(false);
        }

        setFetching(true); setLoadError('');
        try {
            const res = await fetch(`${CUSTOMERS_API}?${params}${withStats ? '&stats=1' : ''}`, { headers, signal: controller.signal });
            const json = await res.json().catch(() => ({}));
            if (!res.ok || !json.success) throw new Error(json.message || 'Could not load clients.');
            setRows(json.data || []); setTotal(json.total || 0); setPages(json.pages || 1);
            if (json.stats) setStats(json.stats);
            writeCache(key, { data: json.data, total: json.total, pages: json.pages, stats: json.stats || stats });
            if (json.page > (json.pages || 1)) setPage(json.pages || 1);
        } catch (e) {
            if (e.name === 'AbortError') return;
            if (!cached) setRows([]);
            setLoadError(e.message === 'Failed to fetch' ? 'Cannot reach the server.' : e.message);
        } finally {
            if (abortRef.current === controller) { setFetching(false); setFirstLoad(false); }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isBranch, storeId, params, headers]);

    // a page change only asks for the rows; the cards come with the first page and after changes
    const lastStatsKey = useRef(-1);
    useEffect(() => {
        const withStats = lastStatsKey.current !== statsKey;
        lastStatsKey.current = statsKey;
        loadPage({ withStats });
    }, [loadPage, statsKey]);

    useEffect(() => { scrollRef.current?.scrollTo({ top: 0 }); }, [page, filter, sort, query, pageSize]);
    useEffect(() => () => abortRef.current?.abort(), []);

    const refreshAll = () => setStatsKey((k) => k + 1);

    /* ---------------- export: every page of the current filter ---------------- */
    const exportExcel = async () => {
        setExporting(true);
        try {
            const all = [];
            for (let p = 1; p <= 1000; p += 1) {
                const qs = new URLSearchParams({ page: String(p), limit: '1000', q: query, filter, sort }).toString();
                const res = await fetch(`${CUSTOMERS_API}?${qs}`, { headers });
                const json = await res.json().catch(() => ({}));
                if (!res.ok || !json.success) throw new Error(json.message || 'Export failed.');
                all.push(...(json.data || []));
                if (p >= (json.pages || 1)) break;
            }
            if (!all.length) { alert('No clients to export.'); return; }
            const data = all.map((c) => ({
                Name: c.name || '',
                Mobile: canSeePhone ? (c.phone || '') : maskPhone(c.phone),
                Email: c.email || '',
                Visits: c.totalVisits || 0,
                'Last visit': c.lastVisit ? formatDate(c.lastVisit) : '',
                ...(canSeeSpend ? { 'Total spent': c.totalSpent || 0, 'Avg per visit': c.avgTicket || 0 } : {}),
                Udhaar: c.dueAmount || 0,
                Wallet: c.walletBalance || 0,
                'Active plans': (c.activeTags || []).join(', '),
            }));
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data), 'Clients');
            XLSX.writeFile(wb, `Clients_${(activeStore?.storeName || 'branch').replace(/[^\w]+/g, '_')}.xlsx`);
        } catch (e) {
            alert(e.message === 'Failed to fetch' ? 'Cannot reach the server.' : e.message);
        } finally { setExporting(false); }
    };

    /* ---------------- add one client ---------------- */
    const addClient = async () => {
        const phone = form.phone.replace(/\D/g, '');
        if (!form.name.trim()) { setFormError('Enter the client name.'); return; }
        if (phone.length !== 10) { setFormError('Enter a 10 digit mobile number.'); return; }
        setSaving(true); setFormError('');
        try {
            const res = await fetch(CUSTOMERS_API, {
                method: 'POST',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: form.name.trim(), phone, email: form.email.trim(), gender: form.gender, storeId }),
            });
            const json = await res.json().catch(() => ({}));
            if (!res.ok || json.success === false) throw new Error(json.message || 'Could not add the client.');
            setAddOpen(false);
            setForm({ name: '', phone: '', email: '', gender: '' });
            setSort('newest'); setFilter('all'); setSearch(''); setPage(1);
            refreshAll();
        } catch (e) {
            setFormError(e.message === 'Failed to fetch' ? 'Cannot reach the server.' : e.message);
        } finally { setSaving(false); }
    };

    const from = total ? (page - 1) * pageSize + 1 : 0;
    const to = Math.min(page * pageSize, total);
    const pageNumbers = useMemo(() => {
        const out = [];
        const start = Math.max(1, Math.min(page - 2, pages - 4));
        for (let p = start; p <= Math.min(pages, start + 4); p += 1) out.push(p);
        return out;
    }, [page, pages]);
    const colSpan = canSeeSpend ? 6 : 5;

    return (
        <div className={styles.page} ref={pageRef} style={pageHeight ? { height: pageHeight } : undefined}>
            {/* ---------------- header ---------------- */}
            <header className={styles.header}>
                <div className={styles.titleBlock}>
                    <h1>Clients {stats && <span className={styles.countChip}>{stats.totalClients.toLocaleString('en-IN')}</span>}</h1>
                    <p>Profiles, visits, udhaar, wallet and plans for every client.</p>
                </div>
                <div className={styles.headerActions}>
                    {canExport && isBranch && (
                        <button type="button" className={styles.ghostBtn} onClick={exportExcel} disabled={exporting}>
                            {exporting ? <Loader2 size={14} className={styles.spin} /> : <Download size={14} />} Export
                        </button>
                    )}
                    {canImport && isBranch && (
                        <button type="button" className={styles.ghostBtn} onClick={() => setImportOpen(true)}><FileUp size={14} /> Import</button>
                    )}
                    {canCreate && isBranch && (
                        <button type="button" className={styles.primaryBtn} onClick={() => { setFormError(''); setAddOpen(true); }}><UserPlus size={14} /> New client</button>
                    )}
                </div>
            </header>

            {/* ---------------- stats ---------------- */}
            <section className={styles.stats}>
                {[
                    { icon: Users, label: 'Total clients', value: stats ? stats.totalClients.toLocaleString('en-IN') : null, sub: stats ? `${stats.newCount} new in 30 days` : '', tone: 'primary' },
                    { icon: AlertCircle, label: 'Udhaar to collect', value: stats ? money(stats.totalDue) : null, sub: stats ? `${stats.dueCount} clients` : '', tone: 'red', go: 'due' },
                    { icon: Wallet, label: 'Khata advance held', value: stats ? money(stats.totalWallet) : null, sub: stats ? `${stats.walletCount} clients` : '', tone: 'green', go: 'wallet' },
                    { icon: Crown, label: 'On active plans', value: stats ? stats.vipCount.toLocaleString('en-IN') : null, sub: 'Packages, prepaid, memberships', tone: 'amber', go: 'vip' },
                ].map((c) => {
                    const Icon = c.icon;
                    return (
                        <button key={c.label} type="button" className={`${styles.stat} ${styles[`tone_${c.tone}`]} ${c.go && filter === c.go ? styles.statOn : ''}`}
                            onClick={() => { if (c.go) { setFilter(filter === c.go ? 'all' : c.go); setPage(1); } }} disabled={!c.go}>
                            <span className={styles.statIcon}><Icon size={17} /></span>
                            <span className={styles.statText}>
                                <small>{c.label}</small>
                                {c.value === null ? <i className={styles.skelLine} /> : <strong>{c.value}</strong>}
                                <em>{c.sub}</em>
                            </span>
                        </button>
                    );
                })}
            </section>

            {/* ---------------- toolbar ---------------- */}
            <div className={styles.toolbar}>
                <div className={styles.search}>
                    <Search size={15} />
                    <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={canSeePhone ? 'Search name, mobile or email' : 'Search by name'} aria-label="Search clients" />
                    {fetching && search ? <Loader2 size={14} className={styles.spin} /> : search && <button type="button" onClick={() => setSearch('')} aria-label="Clear search"><X size={14} /></button>}
                </div>
                <div className={styles.chips} role="tablist" aria-label="Filter clients">
                    {FILTERS.map((f) => (
                        <button key={f.key} type="button" role="tab" aria-selected={filter === f.key} className={filter === f.key ? styles.chipOn : ''} onClick={() => { setFilter(f.key); setPage(1); }}>
                            {f.label}{stats && <b>{(stats[f.stat] || 0).toLocaleString('en-IN')}</b>}
                        </button>
                    ))}
                </div>
                <label className={styles.sort}>
                    <ArrowDownUp size={13} />
                    <select value={sort} onChange={(e) => { setSort(e.target.value); setPage(1); }} aria-label="Sort clients">
                        {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                    </select>
                </label>
            </div>

            {/* ---------------- list (the only part that scrolls) ---------------- */}
            <main className={styles.card}>
                <div className={`${styles.loadingBar} ${fetching && !firstLoad ? styles.loadingOn : ''}`} aria-hidden="true" />
                <div className={styles.scroll} ref={scrollRef}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th className={styles.colClient}>Client</th>
                                <th className={styles.colVisits}>Visits</th>
                                {canSeeSpend && <th className={`${styles.colSpent} ${styles.right}`}>Spent</th>}
                                <th className={`${styles.colMoney} ${styles.right}`}>Udhaar / wallet</th>
                                <th className={styles.colPlans}>Plans</th>
                                <th className={styles.colGo} aria-label="Open" />
                            </tr>
                        </thead>
                        <tbody className={fetching && !firstLoad ? styles.dim : ''}>
                            {!isBranch ? (
                                <tr><td colSpan={colSpan}><div className={styles.empty}><Users size={30} /><h3>Select a branch</h3><p>Clients are kept for each branch.</p></div></td></tr>
                            ) : firstLoad ? (
                                Array.from({ length: 8 }).map((_, i) => (
                                    <tr key={i} className={styles.skelRow}>
                                        <td className={styles.colClient}><div className={styles.clientCell}><i className={styles.skelAvatar} /><span><i className={styles.skelLine} style={{ width: 130 }} /><i className={styles.skelLine} style={{ width: 90 }} /></span></div></td>
                                        <td className={styles.colVisits}><i className={styles.skelLine} style={{ width: 70 }} /></td>
                                        {canSeeSpend && <td className={styles.colSpent}><i className={styles.skelLine} style={{ width: 60, marginLeft: 'auto' }} /></td>}
                                        <td className={styles.colMoney}><i className={styles.skelLine} style={{ width: 60, marginLeft: 'auto' }} /></td>
                                        <td className={styles.colPlans}><i className={styles.skelLine} style={{ width: 90 }} /></td>
                                        <td className={styles.colGo} />
                                    </tr>
                                ))
                            ) : loadError && !rows.length ? (
                                <tr><td colSpan={colSpan}><div className={styles.empty}><AlertCircle size={30} /><h3>Clients could not be loaded</h3><p>{loadError}</p><button type="button" className={styles.ghostBtn} onClick={refreshAll}><RefreshCw size={14} /> Try again</button></div></td></tr>
                            ) : !rows.length ? (
                                <tr><td colSpan={colSpan}>
                                    <div className={styles.empty}>
                                        <UserRoundPlus size={30} />
                                        <h3>{query || filter !== 'all' ? 'No clients match' : 'No clients yet'}</h3>
                                        <p>{query || filter !== 'all' ? 'Try another search or filter.' : 'Add your first client, or bring your whole list from Excel.'}</p>
                                        {(query || filter !== 'all') ? (
                                            <button type="button" className={styles.ghostBtn} onClick={() => { setSearch(''); setFilter('all'); }}>Clear search and filter</button>
                                        ) : canImport && (
                                            <button type="button" className={styles.primaryBtn} onClick={() => setImportOpen(true)}><FileUp size={14} /> Import from Excel / CSV</button>
                                        )}
                                    </div>
                                </td></tr>
                            ) : rows.map((c) => (
                                <tr key={c.id} className={styles.row} onClick={() => navigate(`/clients/${c.id}`)} tabIndex={0}
                                    onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/clients/${c.id}`); }}>
                                    <td className={styles.colClient}>
                                        <div className={styles.clientCell}>
                                            <span className={styles.avatar} style={{ background: `${toneOf(c.name)}1a`, color: toneOf(c.name) }}>{initials(c.name)}</span>
                                            <span className={styles.clientText}>
                                                <strong>{c.name}</strong>
                                                <small>+91 {canSeePhone ? prettyPhone(c.phone) : maskPhone(c.phone)}{c.email ? ` · ${c.email}` : ''}</small>
                                            </span>
                                        </div>
                                    </td>
                                    <td className={styles.colVisits}>
                                        <span className={styles.stack}>
                                            <b>{c.totalVisits || 0} {c.totalVisits === 1 ? 'visit' : 'visits'}</b>
                                            <small title={c.lastVisit ? formatDate(c.lastVisit) : ''}>{ago(c.lastVisit)}</small>
                                        </span>
                                    </td>
                                    {canSeeSpend && (
                                        <td className={`${styles.colSpent} ${styles.right}`}>
                                            <span className={styles.stack}>
                                                <b>{money(c.totalSpent)}</b>
                                                <small>{c.totalVisits ? `${money(c.avgTicket)} / visit` : '—'}</small>
                                            </span>
                                        </td>
                                    )}
                                    <td className={`${styles.colMoney} ${styles.right}`}>
                                        <span className={styles.balances}>
                                            {c.dueAmount > 0 && <span className={styles.due}>{money(c.dueAmount)} due</span>}
                                            {c.walletBalance > 0 && <span className={styles.wallet}>{money(c.walletBalance)} adv</span>}
                                            {!(c.dueAmount > 0) && !(c.walletBalance > 0) && <span className={styles.none}>—</span>}
                                        </span>
                                    </td>
                                    <td className={styles.colPlans}>
                                        {c.activeTags?.length ? (
                                            <span className={styles.tags}>
                                                {c.activeTags.slice(0, 2).map((t) => <span key={t} className={/vip|gold|prepaid/i.test(t) ? styles.tagVip : styles.tag}>{/vip|gold/i.test(t) && <Crown size={9} />}{t}</span>)}
                                                {c.activeTags.length > 2 && <span className={styles.tagMore} title={c.activeTags.slice(2).join(', ')}>+{c.activeTags.length - 2}</span>}
                                            </span>
                                        ) : <span className={styles.none}>No plan</span>}
                                    </td>
                                    <td className={`${styles.colGo} ${styles.goCell}`}><Go size={15} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* ---------------- pager ---------------- */}
                {isBranch && !firstLoad && total > 0 && (
                    <footer className={styles.pager}>
                        <span className={styles.range}><b>{from.toLocaleString('en-IN')}–{to.toLocaleString('en-IN')}</b> of {total.toLocaleString('en-IN')}</span>
                        <div className={styles.pageNav}>
                            <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} aria-label="Previous page"><ChevronLeft size={15} /></button>
                            {pageNumbers.map((n) => <button key={n} type="button" className={n === page ? styles.pageOn : ''} onClick={() => setPage(n)}>{n}</button>)}
                            <button type="button" onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page >= pages} aria-label="Next page"><ChevronRight size={15} /></button>
                        </div>
                        <label className={styles.perPage}>
                            Rows
                            <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
                                {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
                            </select>
                        </label>
                    </footer>
                )}
            </main>

            {importOpen && (
                <BulkImportClients storeId={storeId} canUpdateExisting={canImportUpdate} onClose={() => setImportOpen(false)} onDone={refreshAll} />
            )}

            {/* ---------------- new client ---------------- */}
            {addOpen && (
                <div className={styles.overlay} onMouseDown={(e) => e.target === e.currentTarget && !saving && setAddOpen(false)} role="presentation">
                    <div className={styles.modal} role="dialog" aria-modal="true" aria-label="New client">
                        <div className={styles.modalHead}>
                            <span className={styles.modalIcon}><UserPlus size={17} /></span>
                            <div><h3>New client</h3><p>Name and mobile are enough to start.</p></div>
                            <button type="button" className={styles.modalClose} onClick={() => setAddOpen(false)} aria-label="Close"><X size={16} /></button>
                        </div>
                        <div className={styles.modalBody}>
                            <label className={styles.field}><span>Full name</span>
                                <input autoFocus value={form.name} maxLength={120} placeholder="e.g. Priya Sharma" onChange={(e) => setForm({ ...form, name: e.target.value })} />
                            </label>
                            <label className={styles.field}><span>Mobile number</span>
                                <div className={styles.phoneInput}><i>+91</i><input value={form.phone} inputMode="numeric" maxLength={10} placeholder="10 digit mobile" onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, '') })} /></div>
                            </label>
                            <div className={styles.fieldRow}>
                                <label className={styles.field}><span>Email (optional)</span>
                                    <input type="email" value={form.email} maxLength={120} placeholder="name@email.com" onChange={(e) => setForm({ ...form, email: e.target.value })} />
                                </label>
                                <label className={styles.field}><span>Gender (optional)</span>
                                    <select value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}>
                                        <option value="">Not set</option><option value="Female">Female</option><option value="Male">Male</option><option value="Other">Other</option>
                                    </select>
                                </label>
                            </div>
                            {formError && <p className={styles.formError}><AlertCircle size={13} /> {formError}</p>}
                        </div>
                        <div className={styles.modalFoot}>
                            <button type="button" className={styles.ghostBtn} onClick={() => setAddOpen(false)} disabled={saving}>Cancel</button>
                            <button type="button" className={styles.primaryBtn} onClick={addClient} disabled={saving}>{saving ? <Loader2 size={14} className={styles.spin} /> : <UserPlus size={14} />} {saving ? 'Saving…' : 'Add client'}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Clients;
